import { Button, Form, message, Modal, Select, Space, Table, Tooltip, Typography } from 'antd';
import { useNavigate } from 'react-router-dom';
import { Fragment, useEffect, useState, useRef, useLayoutEffect, useCallback } from 'react';
import { useHorizontalScrollGuard } from '@/hooks/useHorizontalScrollGuard';
import { useFilterPanelVisible } from '@/hooks/useFilterPanelVisible';
import { useTableScrollY, MAIN_TOOLBAR_DEDUCT_SELECTORS } from '@/hooks/useTableScrollY';
import { useVerticalScrollShadow } from '@/hooks/useVerticalScrollShadow';
import { useEnableTableScrollYLayout } from '@/layouts/tableScrollLayout';
import { getEnterprisesListApi, updataEnterprisesListApi } from '@services/Eha';
import copy from 'copy-to-clipboard';
import iconSVip from '@static/images/icon-svip.png';
import iconVip from '@static/images/icon-vip.png';
import iconV from '@static/images/icon-v.png';
import './EnterprisesList.less';
import PageHeader from '@components/PageHeader/PageHeader';
import EnterprisesFilterPanel from './EnterprisesFilterPanel';
import { TABLE_PAGE_SIZE_OPTIONS } from '@/utils/property';

const { Text } = Typography;

export default function EnterprisesList() {
    const navigate = useNavigate();
    const [vipForm] = Form.useForm();
    const { filterToggleProps, FilterPanelSlot } = useFilterPanelVisible();

    const tableRegionRef = useRef(null);
    const tableScrollContainerRef = useRef(null);
    const [scrollContainerVersion, setScrollContainerVersion] = useState(0);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const enableTableScrollYLayout = useEnableTableScrollYLayout();
    const tableScrollY = useTableScrollY(tableRegionRef, {
        enabled: enableTableScrollYLayout || isFullscreen,
        heightRootClassName: 'main-container-card',
        scrollYOffset: 0,
        deductSiblingSelectors: MAIN_TOOLBAR_DEDUCT_SELECTORS,
    });
    useHorizontalScrollGuard(tableScrollContainerRef, { threshold: 6, edgeHoldMs: 220, watchValue: scrollContainerVersion });
    useVerticalScrollShadow(tableScrollContainerRef, { threshold: 2, watchValue: scrollContainerVersion });

    useLayoutEffect(() => {
        const region = tableRegionRef.current;
        if (!region) return;
        const resolveScrollContainer = () => {
            const next = region.querySelector('.ant-table-body') ?? region.querySelector('.ant-table-content');
            if (tableScrollContainerRef.current !== next) {
                tableScrollContainerRef.current = next;
                setScrollContainerVersion(v => v + 1);
            }
        };
        resolveScrollContainer();
        let rafId = 0;
        const mutationObserver = new MutationObserver(() => {
            if (rafId) cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(() => {
                rafId = 0;
                resolveScrollContainer();
            });
        });
        mutationObserver.observe(region, { childList: true, subtree: true });
        return () => {
            if (rafId) cancelAnimationFrame(rafId);
            mutationObserver.disconnect();
        };
    }, []);

    const handleToggleFullscreen = useCallback(() => {
        const target = document.querySelector('main.main-container');
        if (!target) return;
        const next = !target.classList.contains('is-page-fullscreen');
        target.classList.toggle('is-page-fullscreen', next);
        setIsFullscreen(next);
    }, []);

    useEffect(() => {
        const onKeyDown = e => {
            if (e.key !== 'Escape') return;
            const target = document.querySelector('main.main-container.is-page-fullscreen');
            if (!target) return;
            target.classList.remove('is-page-fullscreen');
            setIsFullscreen(false);
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, []);

    useEffect(() => {
        return () => {
            const target = document.querySelector('main.main-container.is-page-fullscreen');
            if (target) target.classList.remove('is-page-fullscreen');
        };
    }, []);

    // 获取列表数据
    const [data, setData] = useState([]);
    const [itemData, setItemData] = useState({});
    const [queryObj, setQueryObj] = useState({});
    // 分页设置
    const [pageSize, setPageSize] = useState(10);
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    // 分页
    const onChangePage = (page, pageSize) => {
        setPage(page);
        setPageSize(pageSize);
    };

    const [isModalOpen, setIsModalOpen] = useState(false);
    const showModal = item => {
        setIsModalOpen(true);
        setItemData(item);
        vipForm.resetFields();
    };
    const handleOk = () => {
        vipForm.validateFields().then(() => {
            const vip = vipForm.getFieldValue('vip');
            updataEnterprisesListApi({ memCard: itemData.memCard, vip: vip }).then(res => {
                if (res.status_code === 200) {
                    message.success(res.message);
                    setIsModalOpen(false);
                    getData();
                }
            });
        });
    };
    const handleCancel = () => {
        setIsModalOpen(false);
    };

    const onFinish = res => {
        setPage(1);
        setQueryObj(res);
    };
    const onReset = () => {
        setPage(1);
        setQueryObj({});
    };

    const getData = useCallback(() => {
        const params = {
            ...queryObj,
            page,
            pageSize,
        };
        if (params.vip === -1) {
            delete params.vip;
        }
        getEnterprisesListApi(params).then(res => {
            if (res && res.status_code === 200) {
                setData(res.data.list);
                setTotal(res.data.totalCount);
            }
        });
    }, [page, pageSize, queryObj]);

    useEffect(() => {
        getData();
    }, [getData]);

    const onSolutionList = item => {
        navigate(`/healthaccount/eha/solutions?memCard=${item.memCard}`);
    };

    const memCardCopy = memCard => {
        copy(memCard);
        message.success('复制memCard成功！');
    };
    const columns = [
        {
            title: '企业用户昵称',
            dataIndex: 'nickName',
            width: 300,
            render: (_, row) => (
                <Fragment>
                    <Tooltip
                        placement="top"
                        title={
                            <span onClick={() => memCardCopy(row.memCard)}>
                                {row.memCard}
                                <i className="bi bi-copy"></i>
                            </span>
                        }
                    >
                        {row.nickName}
                    </Tooltip>
                </Fragment>
            ),
        },
        { title: '企业用户手机', dataIndex: 'memMobile', width: 100 },
        {
            title: '医企+会员',
            dataIndex: 'vip',
            width: 150,
            render: (_, row) => (
                <Fragment>
                    {row.vip === 0 && <span>无等级</span>}
                    {row.vip === 1 && (
                        <span>
                            <img src={iconV} className="icon-vip" />
                            黄金会员
                        </span>
                    )}
                    {row.vip === 2 && (
                        <span>
                            <img src={iconSVip} className="icon-vip" />
                            钻石会员
                        </span>
                    )}
                    {row.vip === 3 && (
                        <span>
                            <img src={iconVip} className="icon-vip" />
                            超级会员
                        </span>
                    )}
                </Fragment>
            ),
        },
        {
            title: (
                <span>
                    解决方案数{' '}
                    <Tooltip placement="top" title={'审核通过数 / 总数（待审核+已通过+已驳回）'}>
                        <i className="bi bi-question-circle"></i>
                    </Tooltip>
                </span>
            ),
            dataIndex: 'total',
            width: 100,
            render: (_, row) => (
                <Fragment>
                    <span className="operate-style">
                        <Text type="success">{row.successTotal}</Text> / <span className="total-style">{row.total}</span>
                    </span>
                </Fragment>
            ),
        },
        { title: '最近发布时间', dataIndex: 'lastPubTime', width: 200 },
        {
            title: '操作',
            dataIndex: 'operations',
            width: 180,
            render: (_, row) => (
                <Fragment>
                    <span className="operate-style" onClick={() => onSolutionList(row)}>
                        管理解决方案
                    </span>
                    <span className="operate-style" onClick={() => showModal(row)}>
                        变更会员等级
                    </span>
                </Fragment>
            ),
        },
    ];

    return (
        <div className="page-container list-page-container eha-enterprises-list-page">
            <PageHeader title="医企+企业管理" breadcrumb={[{ label: '健康号', path: '/healthaccount' }, { label: '医企+企业管理' }]} {...filterToggleProps} />

            <FilterPanelSlot>
                <EnterprisesFilterPanel onFinish={onFinish} onReset={onReset} />
            </FilterPanelSlot>

            <div ref={tableRegionRef} className="table-region-container eha-enterprises-table-region">
                <div className="main-toolbar eha-enterprises-toolbar">
                    <div className="main-tools">
                        <span className="table-title">企业列表</span>
                    </div>
                    <div className="sub-tools">
                        <Space>
                            <Tooltip title="刷新列表">
                                <Button type="default" onClick={getData} icon={<span className="bi bi-arrow-clockwise" aria-hidden="true" />} aria-label="刷新列表" />
                            </Tooltip>
                            <Tooltip title={isFullscreen ? '退出全屏' : '全屏'}>
                                <Button type="default" onClick={handleToggleFullscreen} icon={<span className={isFullscreen ? 'bi bi-fullscreen-exit' : 'bi bi-fullscreen'} aria-hidden="true" />} aria-label={isFullscreen ? '退出全屏' : '全屏'} />
                            </Tooltip>
                        </Space>
                    </div>
                </div>

                <div className="list-table-wrap eha-enterprises-table-wrap">
                    <Table
                        className={tableScrollY != null ? 'custom-table-scrollbar-always' : undefined}
                        rowKey="memCard"
                        columns={columns}
                        dataSource={data}
                        bordered
                        sticky
                        size="medium"
                        pagination={{
                            total: total,
                            showTotal: () => `共${total}条`,
                            current: page,
                            pageSize: pageSize,
                            pageSizeOptions: TABLE_PAGE_SIZE_OPTIONS,
                            onChange: onChangePage,
                            showQuickJumper: true,
                            showSizeChanger: { placement: 'topRight' },
                        }}
                        scroll={{ x: 'max-content', ...(tableScrollY != null ? { y: tableScrollY } : {}) }}
                    />
                </div>
            </div>

            <Modal title="变更医企+会员等级" open={isModalOpen} onOk={handleOk} onCancel={handleCancel}>
                <Form form={vipForm}>
                    <Form.Item name="vip" label="医企+会员" rules={[{ required: true, message: '请选择医企+会员' }]}>
                        <Select
                            placeholder="请选择医企+会员"
                            options={[
                                { value: 0, label: '无等级' },
                                { value: 1, label: '黄金会员' },
                                { value: 2, label: '钻石会员' },
                                { value: 3, label: '超级会员' },
                            ]}
                        ></Select>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
}
